/**
 * The automation flow, drawn and edited the way the redesign confirmed it (flashroom-redesign-2026/c-build/flow.js):
 * the When stop, then each step as a card on one line with a + on every gap, a Branch as a check whose Yes path
 * runs under it and whose No path runs to the side, and an End. A card opens in place with its own controls, a
 * × that closes it, and Delete step, Discard and Done along the bottom. While editing, a step can be dragged to
 * another place. On a phone the flow is an outline whose rows open each step full width.
 *
 * Pure UI over the `Editor` model: the owner passes `editor` in, takes every change back through `setEditor`,
 * and does the saving.
 */
import {
  createContext,
  Fragment,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type {
  AutomationDetail,
  AutomationEnroll,
  AutomationPeople,
  AutomationStatus,
  BranchAsk,
  FactCatalog,
  OfferJson,
  PersonAtBlock,
  StopWhen,
  TriggerJson,
  WaitMode,
  WebinarRepeats,
} from "../../shared/types";
import { sampleHandoff } from "../../shared/handoff-sample";
import { actWords, emailNeeds } from "../../shared/automation-words";
import { FILL_INS, fillRichHtml, fillText, type TokenValues } from "../../shared/tokens";
import {
  isSystemTag,
  legacyTrigger,
  normalizeTag,
  startsEachSession,
  SYSTEM_TAG_NAMES,
  tagCanComeOff,
} from "../../shared/tags";
import { CUSTOMER_TAG } from "../../shared/offer";
import {
  type BranchStep,
  deleteStep,
  type EmailStep,
  graphToTree,
  insertStep,
  type ListPath,
  locateStep,
  mapStep,
  moveStep,
  newStep,
  type NotifyStep,
  type RoomStep,
  type Step,
  type StepType,
  tagsInFlow,
  undeleteStep,
  unfinished,
  type WaitStep,
  type WaitUnit,
  type WebhookStep,
} from "../automations/flow-model";
import { ApiError, api } from "../lib/api";
import { useSendingStatus } from "../sending/SendingContext";
import { heldLine, heldReason } from "../sending/sendingProtection";
import { FlowFiles } from "./FlowFiles";
import { RichBox } from "./RichBox";
import { FaceStack } from "./ui/Face";
import { Icon, type IconName } from "./ui/Icon";
import { Select, type SelectOption } from "./ui/Select";
import { TagPicker } from "./ui/TagPicker";
import { useToast } from "./ui/Toast";
import "../styles/flow.css";

export type {
  BranchStep,
  EmailStep,
  FactStep,
  NotifyStep,
  RoomStep,
  Step,
  WaitStep,
  WebhookStep,
} from "../automations/flow-model";
/** The board's name for a step. */
export type EStep = Step;

// ── editor model ────────────────────────────────────────────────────────
export interface Editor {
  id: string | null;
  name: string;
  status: AutomationStatus;
  trigger: TriggerJson;
  /** The rule that takes a person out of this automation early, or null. */
  stopWhen: StopWhen;
  steps: Step[];
  webinars: Array<{ webinar_id: string; title: string; enabled: boolean; repeats?: WebinarRepeats }>;
  enroll: AutomationEnroll | null;
}

export function editorFromDetail(d: AutomationDetail): Editor {
  const steps: Step[] = graphToTree(d.graph ?? { start: null, nodes: [] });
  return {
    id: d.id,
    name: d.name,
    status: d.status,
    trigger: legacyTrigger(d.trigger).trigger,
    stopWhen: d.stop_when ?? null,
    steps,
    webinars: d.webinars.map((w) => ({
      webinar_id: w.webinar_id,
      title: w.title,
      enabled: w.enabled === 1,
      ...(w.repeats ? { repeats: w.repeats } : {}),
    })),
    enroll: d.enroll,
  };
}

/** The webinars this automation runs on. */
function runsOn(editor: Editor): Editor["webinars"] {
  return editor.webinars.filter((w) => w.enabled);
}

/**
 * Does How often mean anything here? On any room it runs on that comes round again (AUTOMATION-MODEL.md): each
 * session starts it again for everyone who carries the tag when the next session comes round, once does not.
 */
function howOftenMatters(editor: Editor): boolean {
  return runsOn(editor).some((w) => w.repeats);
}

// ── what each kind of step is called and how it looks ─────────────────
type Kind = StepType | "trigger";
const KIND: Record<Kind, { label: string; icon: IconName; tone: "" | "wine" | "brass" }> = {
  trigger: { label: "When", icon: "tag", tone: "brass" },
  email: { label: "Send email", icon: "mail", tone: "wine" },
  room: { label: "Add to their page", icon: "file", tone: "" },
  fact: { label: "Change a tag", icon: "tag", tone: "" },
  webhook: { label: "Send to a webhook", icon: "webhook", tone: "" },
  notify: { label: "Notify a person", icon: "bell", tone: "" },
  wait: { label: "Wait", icon: "clock", tone: "" },
  branch: { label: "Branch", icon: "branch", tone: "brass" },
};
/** The prototype's names for the kinds, kept as class names so its styles apply unchanged. */
const KIND_CLASS: Record<Kind, string> = {
  trigger: "when",
  email: "mail",
  room: "page",
  fact: "tag",
  webhook: "hook",
  notify: "tell",
  wait: "wait",
  branch: "check",
};

/** The step menu, in the order the + offers it, with a rule before Wait. */
const STEP_MENU: StepType[] = ["email", "room", "fact", "webhook", "notify", "wait", "branch"];

export function kindLabel(type: StepType): string {
  return KIND[type].label;
}

export function kindIcon(type: Kind): IconName {
  return KIND[type].icon;
}

export function kindClass(type: Kind): string {
  return KIND_CLASS[type];
}

export function kindTone(type: Kind): "" | "wine" | "brass" {
  return KIND[type].tone;
}

// ── words ──────────────────────────────────────────────────────────────
const UNIT_WORDS: Record<WaitUnit, [string, string]> = {
  minutes: ["minute", "minutes"],
  hours: ["hour", "hours"],
  days: ["day", "days"],
};
const amount = (n: number, unit: WaitUnit) => `${n} ${UNIT_WORDS[unit][n === 1 ? 0 : 1]}`;

/** "For 2 days", "Until 1 hour before the session starts", "Until the session starts". */
export function waitWords(s: Pick<WaitStep, "mode" | "num" | "unit">, short = false): string {
  if (s.mode === "before") {
    if (!s.num) return short ? "Until it starts" : "Until the session starts";
    return short ? `Until ${amount(s.num, s.unit)} before` : `Until ${amount(s.num, s.unit)} before the session starts`;
  }
  return `For ${amount(s.num, s.unit)}`;
}

/** A tag as the flow shows it: Flashroom's own tags are outlined. */
export function TagChip({ name }: { name: string }) {
  if (!name) return <span className="tag fl-notag">no tag yet</span>;
  return <span className={isSystemTag(name) ? "tag tsys" : "tag"}>{name}</span>;
}

function between(a: BranchAsk, unit: string): string {
  return a.cmp === "between" ? `between ${a.a ?? 0} and ${a.b ?? 0}${unit}` : `${a.cmp === "less" ? "less" : "more"} than ${a.a ?? 0}${unit}`;
}

/** The question a branch asks, as words with its tag. */
export function AskWords({ ask }: { ask: BranchAsk }) {
  switch (ask.kind) {
    case "tag":
      return (
        <>
          Has the tag <TagChip name={ask.tag ?? ""} />?
        </>
      );
    case "notag":
      return (
        <>
          Doesn’t have the tag <TagChip name={ask.tag ?? ""} />?
        </>
      );
    case "pct":
      return <>Watched {between(ask, "%")}?</>;
    case "min":
      return <>Watched {between(ask, " minutes")}?</>;
    default:
      return <>More than {ask.a ?? 0} days since they were last active?</>;
  }
}

/** What the Yes and No paths of a branch mean, for their labels. */
export function askPaths(ask: BranchAsk): { yes: string; no: string } {
  const tag = ask.tag || "the tag";
  switch (ask.kind) {
    case "tag":
      return { yes: `has ${tag}`, no: `doesn’t have ${ask.tag || "it"}` };
    case "notag":
      return { yes: `doesn’t have ${tag}`, no: `has ${ask.tag || "it"}` };
    case "pct":
      return { yes: `watched ${between(ask, "%")}`, no: "everyone else" };
    case "min":
      return { yes: `watched ${between(ask, " minutes")}`, no: "everyone else" };
    default:
      return { yes: `away more than ${ask.a ?? 0} days`, no: `active in the last ${ask.a ?? 0} days` };
  }
}

/** The lines under When: how often, and what losing the tag does. */
function triggerLines(trigger: TriggerJson, repeats: boolean): string[] {
  const out: string[] = [];
  if (repeats) out.push(`How often: ${startsEachSession(trigger) ? "each session" : "once"}`);
  if (trigger.type === "tag_added" && tagCanComeOff(trigger.value)) {
    out.push(trigger.keep_going ? "Keeps going if they lose the tag" : "Ends on its own if they lose the tag");
  }
  return out;
}

function StopWords({ stop }: { stop: NonNullable<StopWhen> }) {
  const tags = [stop.tag, ...(stop.more ?? [])].filter(Boolean);
  return (
    <span>
      Stops for anyone who {stop.signal === "not_has_tag" ? "doesn’t have" : "has"}{" "}
      {tags.map((t, i) => (
        <Fragment key={`${t}-${i}`}>
          {i ? " or " : null}
          <TagChip name={t} />
        </Fragment>
      ))}
    </span>
  );
}

/** A step's title on its card, its fill-ins read for the sample person, as the preview does. */
function StepTitle({ step, mail }: { step: Step; mail?: MailContext | undefined }) {
  const read = (text: string) => fillText(text, previewValues(mail));
  switch (step.type) {
    case "email":
      return <>{read(step.subject.trim()) || "No subject yet"}</>;
    case "room":
      return <>{step.title.trim() || "An update with no title yet"}</>;
    case "fact":
      return (
        <>
          {step.op === "remove" ? "Remove" : "Add"} <TagChip name={step.name} />
        </>
      );
    case "webhook":
      return <>{step.url.trim() || "No address yet"}</>;
    case "notify":
      return <>{read(step.subject.trim() || "Update on {{first name}}")}</>;
    case "wait":
      return <>{waitWords(step)}</>;
    case "branch":
      return <AskWords ask={step.ask} />;
  }
}

// ── the tags the pickers offer ──────────────────────────────────────────
interface TagsCtx {
  /** The workspace's own tags: the catalog, plus every tag this flow names, plus any typed in this sitting. */
  own: string[];
  add: (name: string) => void;
}
export const TagsContext = createContext<TagsCtx>({ own: [], add: () => undefined });

let catalogCache: FactCatalog | null = null;
function useFactCatalog(): FactCatalog {
  const [cat, setCat] = useState<FactCatalog>(() => catalogCache ?? { tags: [], details: [], system: [] });
  useEffect(() => {
    if (catalogCache) return;
    let alive = true;
    // Why: a thrown call (a stand-in api without the catalog) must read as "no tags yet", not break the flow.
    Promise.resolve()
      .then(() => api.automations.factCatalog())
      .then((c) => {
        catalogCache = c;
        if (alive) setCat(c);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return cat;
}

/** The tags a flow's pickers offer: the catalog, every tag the flow names, and any typed in this sitting. */
export function useFlowTags(trigger: TriggerJson, stopWhen: StopWhen, steps: Step[]): TagsCtx {
  const catalog = useFactCatalog();
  const [made, setMade] = useState<string[]>([]);
  const own = [...new Set([...catalog.tags, ...tagsInFlow(trigger, stopWhen, steps), ...made].map(normalizeTag))]
    .filter(Boolean)
    .toSorted((a, b) => a.localeCompare(b));
  return { own, add: (name) => setMade((m) => (m.includes(name) ? m : [...m, name])) };
}

/** The tag button: Flashroom's tags and the host's in two groups, and "Add" for a name that is not a tag yet. */
export function FlowTag({ value, onChange, label }: { value: string; onChange: (tag: string) => void; label: string }) {
  const { own, add } = useContext(TagsContext);
  const groups = [
    { label: "Flashroom tags", tags: SYSTEM_TAG_NAMES },
    { label: "Your tags", tags: own.filter((t) => !SYSTEM_TAG_NAMES.includes(t)) },
  ];
  return <TagPicker value={value} groups={groups} onChange={onChange} onAdd={add} label={label} />;
}

// ── small pieces ───────────────────────────────────────────────────────
function Pick({ value, onChange, options, label }: { value: string; onChange: (v: string) => void; options: SelectOption[]; label: string }) {
  return <Select ariaLabel={label} value={value} onChange={onChange} options={options} />;
}

/** A small number box. Typing keeps the cursor: the model only follows a real number. */
function NumInput({ value, onChange, label, least = 0 }: { value: number; onChange: (n: number) => void; label: string; least?: number }) {
  const [text, setText] = useState(String(value));
  useEffect(() => {
    setText((t) => (Number(t) === value ? t : String(value)));
  }, [value]);
  return (
    <input
      className="inp fe-num"
      type="number"
      min={least}
      inputMode="numeric"
      aria-label={label}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(Math.max(least, Number(e.target.value) || 0));
      }}
    />
  );
}

function Text({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label className="fe-fld">
      <span>{label}</span>
      <input dir="auto" className="inp" value={value} placeholder={placeholder} autoComplete="off" onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

const UNIT_OPTS: SelectOption[] = [
  { value: "minutes", label: "minutes" },
  { value: "hours", label: "hours" },
  { value: "days", label: "days" },
];
const TRIGGER_OPTS: SelectOption[] = [
  { value: "tag_added", label: "gets the tag" },
  { value: "tag_removed", label: "loses the tag" },
];
const LOST_OPTS: SelectOption[] = [
  { value: "end", label: "ends on its own" },
  { value: "keep", label: "keeps going" },
];
const STOP_OPTS: SelectOption[] = [
  { value: "has_tag", label: "has the tag" },
  { value: "not_has_tag", label: "doesn’t have the tag" },
];
const ASK_OPTS: SelectOption[] = [
  { value: "tag", label: "has the tag" },
  { value: "notag", label: "doesn’t have the tag" },
  { value: "pct", label: "Watched %" },
  { value: "min", label: "Minutes watched" },
  { value: "idle", label: "Days since last active" },
];
const CMP_OPTS: SelectOption[] = [
  { value: "more", label: "more than" },
  { value: "less", label: "less than" },
  { value: "between", label: "between" },
];

// ── the email as Priya gets it ──────────────────────────────────────────
/** What the preview fills a sample email with. Absent for a workspace automation. */
export interface MailContext {
  webinarTitle?: string;
  hostName?: string;
  workspaceName?: string;
  /** The address the email comes from, when the workspace has one. */
  fromEmail?: string;
  /** The session's date and time, already written out; empty for a talk on demand. */
  startsAt?: string;
  offer?: OfferJson | null;
  /** The workspace's brand colours, so the preview's button matches the sent one. */
  brand?: { accent?: string; brass?: string } | null;
}

/** The person the preview writes to, so the host sees real words, not fill-ins. */
const SAMPLE = { firstName: "Priya", lastName: "Raman", email: "priya.raman@gmail.com" };

function previewValues(mail: MailContext | undefined): TokenValues {
  return {
    ...SAMPLE,
    webinarTitle: mail?.webinarTitle ?? "",
    hostName: mail?.hostName ?? "",
    workspaceName: mail?.workspaceName ?? "",
    startsAt: mail?.startsAt ?? "",
    // Links that go nowhere: this is a sample, not anyone's real page.
    privatePage: "#",
    watchPage: "#",
    unsubscribe: "#",
    plan: "Twice a week",
    video: "#",
    offer: mail?.offer ?? null,
    offerLink: mail?.offer?.url ?? "#",
    brand: mail?.brand ?? null,
  };
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

function MailPreview({ step, mail }: { step: EmailStep; mail: MailContext | undefined }) {
  const values = previewValues(mail);
  const from = mail?.hostName || mail?.workspaceName || "You";
  const business = mail?.workspaceName || from;
  const accent = mail?.brand?.accent ?? "var(--wine)";
  return (
    <div className="fe-pv">
      <div className="bw-mwin">
        <div className="bw-mwin-h">
          <span className="bw-mwin-d" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <span className="bw-mwin-t">
            <Icon name="inbox" />
            Priya’s inbox
          </span>
        </div>
        <article className="bw-mail in" style={{ ["--pa" as string]: accent }}>
          <div className="bw-mh">
            <dl className="bw-mhd">
              <dt>From</dt>
              <dd>
                <b>{from}</b>
                {mail?.fromEmail ? <span className="bw-mfrom"> &lt;{mail.fromEmail}&gt;</span> : null}
              </dd>
              <dt>To</dt>
              <dd>
                Priya Raman &lt;{SAMPLE.email}&gt;
              </dd>
              <dt>Subject</dt>
              <dd className="bw-msubw">
                <span className="bw-msub">{fillText(step.subject, values) || "No subject yet"}</span>
              </dd>
            </dl>
          </div>
          <div className="bw-mb">
            <div className="bw-mbrand">
              <span>{initials(business)}</span>
              {business}
            </div>
            <div className="bw-mp" dangerouslySetInnerHTML={{ __html: fillRichHtml(step.previewHtml, values) }} />
            {step.images.map((a, i) =>
              a.key ? <img className="fe-pvimg" key={`${a.key}-${i}`} src={`/api/assets/${a.key}`} alt="" /> : null,
            )}
            {step.attachments.map((a, i) => (
              <div className="fe-file in" key={`${a.key ?? a.name}-${i}`}>
                <Icon name="file" />
                <span>{a.name}</span>
              </div>
            ))}
            {step.button.trim() ? <div className="bw-mbtn">{fillText(step.button, values)}</div> : null}
            <div className="bw-mft">
              {business} · <u>Unsubscribe</u>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}

// ── each step's controls ────────────────────────────────────────────────
type Patch<S extends Step> = (patch: Partial<S>) => void;

function EmailFields({ step, onChange, onToast, mail }: { step: EmailStep; onChange: Patch<EmailStep>; onToast: (m: string) => void; mail: MailContext | undefined }) {
  const putIn = useRef<((text: string) => void) | null>(null);
  const [fills, setFills] = useState(false);
  const [preview, setPreview] = useState(false);
  const offered = FILL_INS.filter((f) => (f.key !== "offer link" && f.key !== "offer words") || !!mail?.offer);
  return (
    <>
      <Text label="Subject" value={step.subject} onChange={(subject) => onChange({ subject })} />
      <div className="fe-fld">
        <div className="fe-fh">
          <span>Message</span>
          <button type="button" className="link fe-link" aria-expanded={fills} onClick={() => setFills((v) => !v)}>
            <Icon name="plus" />
            Fill-ins
          </button>
        </div>
        {fills ? (
          <div className="fe-chips fill">
            {offered.map((f) => (
              <button type="button" className="fe-chip" key={f.token} onClick={() => putIn.current?.(f.token)}>
                {f.token.slice(2, -2)}
              </button>
            ))}
          </div>
        ) : null}
        <RichBox
          bare
          className="inp fe-msg"
          value={step.previewHtml}
          onChange={(html) => onChange({ previewHtml: html })}
          insertRef={putIn}
          ariaLabel="Message"
          placeholder=""
        />
      </div>
      <Text label="Button" value={step.button} placeholder="Optional" onChange={(button) => onChange({ button })} />
      <FlowFiles label="Pictures in the email" fits="image" value={step.images} onChange={(images) => onChange({ images })} onToast={onToast} />
      <FlowFiles label="Attachments" value={step.attachments} onChange={(attachments) => onChange({ attachments })} onToast={onToast} />
      <div className="fe-row">
        <button type="button" className={preview ? "btn s on" : "btn s"} aria-pressed={preview} onClick={() => setPreview((v) => !v)}>
          <Icon name="eye" />
          {preview ? "Hide the preview" : "Preview with Priya"}
        </button>
      </div>
      {preview ? <MailPreview step={step} mail={mail} /> : null}
    </>
  );
}

function RoomFields({ step, onChange, onToast }: { step: RoomStep; onChange: Patch<RoomStep>; onToast: (m: string) => void }) {
  return (
    <>
      <Text label="Title" value={step.title} onChange={(title) => onChange({ title })} />
      <div className="fe-fld">
        <span>Note</span>
        <RichBox bare className="inp fe-msg short" value={step.noteHtml} onChange={(noteHtml) => onChange({ noteHtml })} ariaLabel="Note" placeholder="" />
      </div>
      <FlowFiles label="A clip, a voice note or files" value={step.attachments} onChange={(attachments) => onChange({ attachments })} onToast={onToast} />
      <label className="fe-check">
        <input type="checkbox" checked={step.notifyEnabled} onChange={(e) => onChange({ notifyEnabled: e.target.checked })} />
        Email them a link
      </label>
      {step.notifyEnabled ? <Text label="Its subject" value={step.notifySubject} placeholder={step.title} onChange={(notifySubject) => onChange({ notifySubject })} /> : null}
    </>
  );
}

/** Posts a sample to the address and says how it went. */
export function sendWebhookTest(address: string, onToast: (m: string) => void): void {
  const url = address.trim();
  if (!url) {
    onToast("Enter the address first");
    return;
  }
  api.connections
    .testWebhook({ url })
    .then((r) => onToast(r.ok ? "Sent a test" : `Your app answered ${r.status}`))
    .catch((err) => onToast(err instanceof ApiError ? err.message : "Your app could not be reached"));
}

function WebhookFields({ step, onChange, onToast }: { step: WebhookStep; onChange: Patch<WebhookStep>; onToast: (m: string) => void }) {
  const test = () => sendWebhookTest(step.url, onToast);
  return (
    <>
      <label className="fe-fld">
        <span>Send to this address</span>
        <input dir="auto"
          className="inp"
          type="url"
          inputMode="url"
          autoCapitalize="off"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          value={step.url}
          placeholder="https://"
          onChange={(e) => onChange({ url: e.target.value })}
        />
      </label>
      <div className="fe-row">
        <button type="button" className="btn s" onClick={test}>
          <Icon name="send" />
          Send a test
        </button>
      </div>
      <div className="fe-fld">
        <span>What it sends</span>
        <pre className="fe-code">{JSON.stringify(sampleHandoff(Math.floor(Date.now() / 1000)), null, 2)}</pre>
      </div>
    </>
  );
}

function NotifyFields({ step, onChange }: { step: NotifyStep; onChange: Patch<NotifyStep> }) {
  return (
    <>
      <Text label="To" value={step.email} placeholder="you@yourcompany.com, someone@else.com" onChange={(email) => onChange({ email })} />
      <Text label="Subject" value={step.subject} placeholder="Update on {{first name}}" onChange={(subject) => onChange({ subject })} />
      <label className="fe-fld">
        <span>Message</span>
        <textarea dir="auto" className="inp" rows={3} value={step.message} onChange={(e) => onChange({ message: e.target.value })} />
      </label>
    </>
  );
}

function WaitFields({ step, onChange, sessions }: { step: WaitStep; onChange: Patch<WaitStep>; sessions: boolean | undefined }) {
  const until = step.mode === "before";
  return (
    <>
      <div className="fe-sent">
        <Pick
          label="For or until"
          value={until ? "before" : "after"}
          options={[
            { value: "after", label: "For" },
            { value: "before", label: "Until" },
          ]}
          onChange={(v) => onChange({ mode: v as WaitMode, num: v === "after" ? Math.max(1, step.num) : step.num })}
        />
        <NumInput label="How many" value={step.num} least={until ? 0 : 1} onChange={(num) => onChange({ num })} />
        <Pick label="Unit" value={step.unit} options={UNIT_OPTS} onChange={(v) => onChange({ unit: v as WaitUnit })} />
        <span>{until ? "before the session starts" : "after the step before"}</span>
      </div>
      {until && sessions === false ? <div className="fe-hint">Skipped here: this room plays any time.</div> : null}
    </>
  );
}

function BranchFields({ step, onChange, onSwap }: { step: BranchStep; onChange: (ask: BranchAsk) => void; onSwap: () => void }) {
  const a = step.ask;
  const tagAsk = a.kind === "tag" || a.kind === "notag";
  return (
    <>
      <div className="fe-sent">
        Ask
        <Pick label="Ask about" value={a.kind} options={ASK_OPTS} onChange={(v) => onChange({ ...a, kind: v as BranchAsk["kind"] })} />
        {tagAsk ? <FlowTag label="The tag to ask about" value={a.tag ?? ""} onChange={(tag) => onChange({ ...a, tag })} /> : null}
      </div>
      {tagAsk ? null : a.kind === "idle" ? (
        <div className="fe-sent">
          More than <NumInput label="Days" value={a.a ?? 0} onChange={(n) => onChange({ ...a, a: n })} /> days
        </div>
      ) : (
        <div className="fe-sent">
          <Pick label="Compare" value={a.cmp ?? "more"} options={CMP_OPTS} onChange={(v) => onChange({ ...a, cmp: v as BranchAsk["cmp"] })} />
          <NumInput label="The number" value={a.a ?? 0} onChange={(n) => onChange({ ...a, a: n })} />
          {a.cmp === "between" ? (
            <>
              <span>and</span>
              <NumInput label="The second number" value={a.b ?? 0} onChange={(n) => onChange({ ...a, b: n })} />
            </>
          ) : null}
          <span>{a.kind === "pct" ? "%" : "minutes"}</span>
        </div>
      )}
      <div className="fe-row">
        <button type="button" className="btn s" onClick={onSwap}>
          <Icon name="refresh" />
          Swap the paths
        </button>
      </div>
    </>
  );
}

type TriggerPatch = (patch: Partial<Pick<Editor, "trigger" | "stopWhen">>) => void;

/** The Start card: Starts, then Stops early, each in its own group. */
function TriggerFields({ editor, onChange }: { editor: Editor; onChange: TriggerPatch }) {
  const t = editor.trigger;
  const stop = editor.stopWhen;
  const tags = stop ? [stop.tag, ...(stop.more ?? [])] : [];
  const setTags = (next: string[]) => {
    if (!stop) return;
    const [first = "", ...more] = next;
    onChange({ stopWhen: more.length ? { signal: stop.signal, tag: first, more } : { signal: stop.signal, tag: first } });
  };
  return (
    <>
      <div className="fe-grp">
        <p className="fe-gl">Starts</p>
        <div className="fe-sent">
          When someone
          <Pick
            label="When someone"
            value={t.type}
            options={TRIGGER_OPTS}
            onChange={(v) => onChange({ trigger: { type: v === "tag_removed" ? "tag_removed" : "tag_added", value: t.value } })}
          />
          <FlowTag label="The tag that starts it" value={t.value} onChange={(value) => onChange({ trigger: { ...t, value } })} />
        </div>
        {howOftenMatters(editor) ? (
          <div className="fe-row">
            <span className="fe-l">How often</span>
            <div className="seg" role="radiogroup" aria-label="How often">
              {(["each", "once"] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={(startsEachSession(t) ? "each" : "once") === v}
                  className={(startsEachSession(t) ? "each" : "once") === v ? "on" : ""}
                  onClick={() => onChange({ trigger: { ...t, how_often: v } })}
                >
                  {v === "each" ? "Each session" : "Once"}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {t.type === "tag_added" && tagCanComeOff(t.value) ? (
          <div className="fe-sent">
            If they lose it, this automation
            <Pick
              label="If they lose it"
              value={t.keep_going ? "keep" : "end"}
              options={LOST_OPTS}
              onChange={(v) => onChange({ trigger: v === "keep" ? { ...t, keep_going: true } : { type: t.type, value: t.value, ...(t.how_often ? { how_often: t.how_often } : {}) } })}
            />
          </div>
        ) : null}
      </div>
      <div className="fe-grp">
        <p className="fe-gl">Stops early</p>
        <div className="fe-sent fe-stopq">
          <label className="fe-check">
            <input
              type="checkbox"
              checked={!!stop}
              onChange={(e) => onChange({ stopWhen: e.target.checked ? { signal: "has_tag", tag: CUSTOMER_TAG } : null })}
            />
            Stop for anyone who
          </label>
          {stop ? (
            <>
              <Pick
                label="Stop for anyone who"
                value={stop.signal}
                options={STOP_OPTS}
                onChange={(v) => onChange({ stopWhen: { ...stop, signal: v === "not_has_tag" ? "not_has_tag" : "has_tag" } })}
              />
              {tags.map((tag, i) => (
                <Fragment key={`stop-${i}`}>
                  {i ? <span>or</span> : null}
                  <span className="fe-or">
                    <FlowTag
                      label={i ? `Or the tag ${i + 1}` : "The tag that stops it"}
                      value={tag}
                      onChange={(next) => setTags(tags.map((x, j) => (j === i ? next : x)))}
                    />
                    {i ? (
                      <button type="button" className="ibtn sm" aria-label={`Take ${tag || "this tag"} off`} title={`Take ${tag || "this tag"} off`} onClick={() => setTags(tags.filter((_x, j) => j !== i))}>
                        <Icon name="x" />
                      </button>
                    ) : null}
                  </span>
                </Fragment>
              ))}
            </>
          ) : (
            <span className="fe-dim">has a tag</span>
          )}
        </div>
        {stop ? (
          <button type="button" className="link fe-link" onClick={() => setTags([...tags, ""])}>
            <Icon name="plus" />
            Or another tag
          </button>
        ) : null}
      </div>
    </>
  );
}

// ── who is where, and what a step did ───────────────────────────────────
type Held = { line: string; tone: "warn" | "bad" } | null;

/** What a step did once there are runs: "1,540 sent · 1,093 opened", "708 tagged". */
export function stepFact(step: Step, people: AutomationPeople | null): string {
  const sends = people?.sends[step.uid];
  const posted = people?.posts[step.uid];
  const act = people?.acts?.[step.uid];
  const heldBack = sends?.held ? ` · ${sends.held.toLocaleString("en-US")} held back` : "";
  let sub = "";
  if (step.type === "email" && sends) {
    // An open is FlashEmail's "opened" event, or a click, which means it was opened.
    sub = `${sends.sent.toLocaleString("en-US")} sent · ${sends.opened.toLocaleString("en-US")} opened${heldBack}`;
  } else if (step.type === "room") {
    sub = posted ? `${posted.toLocaleString("en-US")} posted${heldBack}` : step.notifyEnabled ? "Emails them the link" : "";
  } else if (act?.n && (step.type === "fact" || step.type === "notify" || step.type === "webhook" || step.type === "branch")) {
    sub = actWords(step.type, act, step.type === "fact" ? { op: step.op } : step.type === "branch" ? { ask: step.ask } : {});
  } else if (step.type === "notify") {
    sub = "To your team";
  }
  return sub;
}

/** The line under a step's title: what it did once there are runs, and why its email is held. */
function useStepFacts(step: Step, people: AutomationPeople | null): { sub: string; held: Held } {
  const reason = heldReason(useSendingStatus());
  const roomEmails = step.type === "room" && step.notifyEnabled;
  const sub = stepFact(step, people);
  const emailsHere = step.type === "email" || step.type === "notify" || roomEmails;
  const held = reason && emailsHere ? { line: heldLine(reason, step.type as "room" | "email" | "notify"), tone: reason === "paused" ? ("bad" as const) : ("warn" as const) } : null;
  return { sub, held };
}

const facesOf = (people: PersonAtBlock[]) => people.map((p) => ({ name: p.name || p.email, photo: p.avatar_url }));

const DAY_PART = (h: number) => (h < 12 ? "morning" : h < 17 ? "afternoon" : "evening");

/** When the first of them moves on, said the way a person would: "until this afternoon", "until Thursday". */
export function untilWords(at: number, now = Date.now()): string {
  const when = new Date(at * 1000);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const days = Math.floor((when.getTime() - today.getTime()) / 86_400_000);
  if (days <= 0) return when.getHours() >= 17 ? "until tonight" : `until this ${DAY_PART(when.getHours())}`;
  if (days === 1) return `until tomorrow ${DAY_PART(when.getHours())}`;
  if (days < 7) return `until ${when.toLocaleDateString("en-US", { weekday: "long" })}`;
  return `until ${when.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

function HereBar({ people, waits, paused }: { people: PersonAtBlock[]; waits?: boolean; paused?: boolean }) {
  if (!people.length) return null;
  const next = waits ? Math.min(...people.map((p) => p.wakes_at ?? Number.POSITIVE_INFINITY)) : Number.POSITIVE_INFINITY;
  return (
    <div className="herebar">
      <FaceStack people={facesOf(people.slice(0, 3))} size={24} />
      <span>
        <b>{people.length.toLocaleString("en-US")} here now</b>
        {Number.isFinite(next) ? `, ${untilWords(next)}` : ""}
        {paused ? ", paused" : ""}
      </span>
    </div>
  );
}

// ── the flow editor ─────────────────────────────────────────────────────
/**
 * The phone's shape of the flow: an outline whose rows open each step full width. `open` names that step
 * ("trigger" for the When stop), or is null while the outline shows.
 */
export interface FlowOutline {
  open: string | null;
  onOpen: (uid: string) => void;
  onClose: () => void;
}

/** Where "Send email deleted" with its Undo goes. */
type ShowUndo = (message: string, undo?: () => void) => void;

export interface FlowEditorProps {
  editor: Editor;
  setEditor: (e: Editor | ((prev: Editor) => Editor)) => void;
  onToast: (m: string) => void;
  /** What an email preview fills in. Absent for a workspace automation. */
  mail?: MailContext;
  /**
   * When the flow is written by Done rather than by a save of its own (the board): `dirty` says there is
   * something to write, and `onDone` writes it. It is also called after a step is deleted, moved or put back.
   */
  dirty?: boolean;
  onDone?: () => void | Promise<unknown>;
  /** Kept for the board's call; a card's Discard goes back to how the flow was when the card opened. */
  onDiscard?: () => void;
  /** Where "Send email deleted" with its Undo goes. Absent: the app's toast. */
  undo?: ShowUndo;
  /** Who is where in this flow, when the page has read it; with the automation the runs belong to. */
  people?: AutomationPeople | null;
  automationId?: string | null;
  /** Draw the phone's outline instead of the canvas. */
  outline?: FlowOutline;
  /** False draws the flow to read: no +, no cards opening, no dragging. */
  editing?: boolean;
  /** False when none of the rooms it runs on has a session, so a wait before the session is skipped. */
  sessions?: boolean;
  /** A line under When, such as "1,540 joined so far". */
  whenSub?: string;
  /** The automation is off: people wait where they are. */
  paused?: boolean;
  /** The card open as the flow appears: "trigger" for a new follow-up. */
  initialOpen?: string | null;
}

/** Whether `open` names something in this flow that a screen can show. */
function outlineScreen(editor: Editor, open: string | null): "trigger" | NonNullable<ReturnType<typeof locateStep>> | null {
  if (!open) return null;
  if (open === "trigger") return "trigger";
  return locateStep(editor.steps, open);
}

const listKey = (path: ListPath) => path.map((p) => `${p.uid}/${p.arm}`).join(">") || "main";

/** Does the flow hold a branch? Then it takes two columns. A branch ends its line, so one shows on the main line. */
function hasBranch(steps: Step[]): boolean {
  return steps.some((s) => s.type === "branch");
}

/** Everything the parts of one drawn flow share. */
interface FlowCtx {
  editor: Editor;
  editing: boolean;
  open: string | null;
  fresh: string | null;
  menuAt: string | null;
  setMenuAt: (at: string | null) => void;
  people: AutomationPeople | null;
  sessions: boolean | undefined;
  paused: boolean;
  mail: MailContext | undefined;
  onToast: (m: string) => void;
  dragging: string | null;
  repeats: boolean;
  openCard: (id: string) => void;
  done: () => boolean;
  discard: () => void;
  remove: (uid: string) => void;
  insert: (path: ListPath, index: number, type: StepType) => void;
  patchStep: <S extends Step>(uid: string, patch: Partial<S>) => void;
  swap: (uid: string) => void;
  onTrigger: TriggerPatch;
  onNodeDown: (e: ReactPointerEvent<HTMLDivElement>, step: Step) => void;
  onNodeClick: (id: string) => void;
}
const FlowContext = createContext<FlowCtx | null>(null);
function useFlow(): FlowCtx {
  const ctx = useContext(FlowContext);
  if (!ctx) throw new Error("A flow part was drawn outside its flow.");
  return ctx;
}

interface Drag {
  uid: string;
  x: number;
  y: number;
  moved: boolean;
  node: HTMLElement;
  target: { path: ListPath; before: string | null } | null;
}

export function FlowEditor({
  editor,
  setEditor,
  onToast,
  mail,
  dirty,
  onDone,
  undo,
  people: peopleIn,
  outline,
  editing = true,
  sessions,
  whenSub,
  paused = false,
  initialOpen = null,
}: FlowEditorProps) {
  // Only a whole answer is drawn; a half one (a page still loading, a stub) draws nothing.
  const people = peopleIn && peopleIn.totals && peopleIn.at ? peopleIn : null;
  const toast = useToast();
  const [open, setOpen] = useState<string | null>(initialOpen);
  const [fresh, setFresh] = useState<string | null>(null);
  const [menuAt, setMenuAt] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const snap = useRef<Editor | null>(initialOpen ? editor : null);
  const latest = useRef(editor);
  latest.current = editor;
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const root = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  const dropped = useRef(false);

  // Why one write at a time, then the newest: a delete and its Undo sent at once can land in either order,
  // and the server would keep the delete (specs/formal/automation-edit).
  const writing = useRef({ busy: false, again: false });
  const write = useRef(() => {
    const w = writing.current;
    const send = doneRef.current;
    if (!send) return;
    // Why: a move or an Undo while a card still needs something waits for that card's Done, which writes it all.
    if (unfinished(latest.current.trigger, latest.current.steps)) return;
    if (w.busy) {
      w.again = true;
      return;
    }
    w.busy = true;
    Promise.resolve(send())
      .catch(() => undefined)
      .finally(() => {
        w.busy = false;
        if (w.again) {
          w.again = false;
          write.current();
        }
      });
  });
  // The board writes on Done; a delete, a move or an undo is written the same way once React has drawn it.
  const writeSoon = useCallback(() => {
    if (doneRef.current) window.setTimeout(() => write.current(), 0);
  }, []);

  const showUndo: ShowUndo = useCallback(
    (message, back) => {
      if (undo) undo(message, back);
      else toast(back ? { text: message, action: { label: "Undo", onClick: back } } : message);
    },
    [undo, toast],
  );

  const tags = useFlowTags(editor.trigger, editor.stopWhen, editor.steps);

  const setSteps = useCallback((fn: (steps: Step[]) => Step[]) => setEditor((prev) => ({ ...prev, steps: fn(prev.steps) })), [setEditor]);

  /**
   * Close the open card, keeping what was changed in it (Done, ×, Esc). It says no while the card still needs
   * something, as the design's does (c-build/flow.js `feClose`), so an unfinished step is never written.
   */
  const done = useCallback((): boolean => {
    const missing = open ? unfinished(latest.current.trigger, latest.current.steps, open) : null;
    if (missing) {
      onToast(missing.words);
      return false;
    }
    if (dirty) write.current();
    setOpen(null);
    setFresh(null);
    snap.current = null;
    outline?.onClose();
    return true;
  }, [dirty, outline, open, onToast]);

  // Why: the page's own Done ends editing; a card left open would stay drawn with controls nothing saves.
  useEffect(() => {
    if (editing) return;
    setOpen(null);
    setFresh(null);
    setMenuAt(null);
    snap.current = null;
  }, [editing]);

  const openCard = (id: string) => {
    if (!editing) return;
    if (open && open !== id && !outline && !done()) return;
    snap.current = latest.current;
    setOpen(id);
    setMenuAt(null);
    outline?.onOpen(id);
  };

  const discard = () => {
    if (snap.current) setEditor(snap.current);
    setOpen(null);
    setFresh(null);
    snap.current = null;
    outline?.onClose();
  };

  const insert = (path: ListPath, index: number, type: StepType) => {
    if (open && !outline && !done()) return;
    const step = newStep(type);
    snap.current = latest.current;
    setSteps((s) => insertStep(s, path, index, step));
    setMenuAt(null);
    setFresh(step.uid);
    setOpen(step.uid);
    outline?.onOpen(step.uid);
  };

  const remove = (uid: string) => {
    const at = locateStep(latest.current.steps, uid);
    if (!at) return;
    setSteps((s) => deleteStep(s, at.path, uid));
    setOpen(null);
    setFresh(null);
    snap.current = null;
    outline?.onClose();
    writeSoon();
    showUndo(`${KIND[at.step.type].label} deleted`, () => {
      setSteps((s) => undeleteStep(s, at.path, at.index, at.step));
      writeSoon();
    });
  };

  // Esc closes an open card, unless a list or a window is open over it.
  useEffect(() => {
    if (!open || outline) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (document.querySelector(".tsel-pop, .fr-select-list, .fe-menu, [role='dialog'], #as-layer *")) return;
      done();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, outline, done]);

  // The + menu closes on a click anywhere else, and on Esc.
  useEffect(() => {
    if (menuAt === null) return;
    const close = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest?.(".fe-menu, .fl-add, .flp-add")) setMenuAt(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuAt(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuAt]);

  // ── dragging a step to another place ──
  const onNodeDown = (e: ReactPointerEvent<HTMLDivElement>, step: Step) => {
    if (!editing || outline || step.type === "branch" || e.button !== 0) return;
    if ((e.target as HTMLElement).closest("button, a, input, select, textarea")) return;
    drag.current = { uid: step.uid, x: e.clientX, y: e.clientY, moved: false, node: e.currentTarget, target: null };
  };
  const onNodeClick = (id: string) => {
    // A drag ends with a click on the step it dropped; that click is the drop, not an open.
    if (dropped.current) {
      dropped.current = false;
      return;
    }
    openCard(id);
  };
  const moveRef = useRef<(e: PointerEvent) => void>(() => undefined);
  const upRef = useRef<() => void>(() => undefined);
  moveRef.current = (e: PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.x, e.clientY - d.y) < 6) return;
    if (!d.moved) {
      d.moved = true;
      setDragging(d.uid);
    }
    d.node.style.transform = `translate(${e.clientX - d.x}px, ${e.clientY - d.y}px)`;
    for (const el of root.current?.querySelectorAll(".fl-drop") ?? []) el.classList.remove("fl-drop", "fl-drop-after");
    d.target = null;
    const nodes = [...(root.current?.querySelectorAll<HTMLElement>(".fl-node[data-fl][data-list]") ?? [])].filter((n) => n.dataset.fl !== d.uid);
    const hit = nodes.find((n) => {
      const r = n.getBoundingClientRect();
      return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    });
    if (!hit) return;
    const r = hit.getBoundingClientRect();
    const after = e.clientY > r.top + r.height / 2;
    const path = JSON.parse(hit.dataset.list ?? "[]") as ListPath;
    const list = path.reduce<Step[]>((l, p) => {
      const b = l.find((s) => s.uid === p.uid);
      return b && b.type === "branch" ? b[p.arm] : [];
    }, latest.current.steps);
    const i = list.findIndex((s) => s.uid === hit.dataset.fl);
    if (i < 0) return;
    const target = list[i];
    // A step dropped after a branch would run past the end of its line, so a branch only takes a step before it.
    if (after && target.type === "branch") return;
    hit.classList.add("fl-drop");
    if (after) hit.classList.add("fl-drop-after");
    d.target = { path, before: after ? (list[i + 1]?.uid ?? null) : target.uid };
  };
  upRef.current = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    d.node.style.transform = "";
    for (const el of root.current?.querySelectorAll(".fl-drop") ?? []) el.classList.remove("fl-drop", "fl-drop-after");
    setDragging(null);
    if (!d.moved) return;
    dropped.current = true;
    window.setTimeout(() => {
      dropped.current = false;
    }, 0);
    if (!d.target) return;
    const before = latest.current.steps;
    const next = moveStep(before, d.uid, d.target.path, d.target.before);
    if (next === before) return;
    setSteps(() => next);
    writeSoon();
    showUndo("Moved", () => {
      setSteps(() => before);
      writeSoon();
    });
  };
  useEffect(() => {
    if (!editing || outline) return;
    const move = (e: PointerEvent) => moveRef.current(e);
    const up = () => upRef.current();
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
    return () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
    };
  }, [editing, outline]);

  const ctx: FlowCtx = {
    editor,
    editing,
    open,
    fresh,
    menuAt,
    setMenuAt,
    people,
    sessions,
    paused,
    mail,
    onToast,
    dragging,
    repeats: howOftenMatters(editor),
    openCard,
    done,
    discard,
    remove,
    insert,
    patchStep: (uid, patch) => setSteps((s) => mapStep(s, uid, (st) => ({ ...st, ...patch }) as Step)),
    swap: (uid) => setSteps((s) => mapStep(s, uid, (st) => (st.type === "branch" ? { ...st, yes: st.no, no: st.yes } : st))),
    onTrigger: (patch) => setEditor((prev) => ({ ...prev, ...patch })),
    onNodeDown,
    onNodeClick,
  };

  return (
    <TagsContext.Provider value={tags}>
      <FlowContext.Provider value={ctx}>
        {outline ? (
          <Outline open={outline.open} />
        ) : (
          <div ref={root} className={hasBranch(editor.steps) ? "fl" : "fl fl-one"}>
            <WhenStop whenSub={whenSub} />
            {editor.stopWhen && open !== "trigger" ? (
              <div className="fl-stop">
                <Icon name="pause" />
                <StopWords stop={editor.stopWhen} />
              </div>
            ) : null}
            <FlowList list={editor.steps} path={[]} />
          </div>
        )}
      </FlowContext.Provider>
    </TagsContext.Provider>
  );
}

/** One step's controls, by kind. */
function StepFields({ step }: { step: Step }) {
  const f = useFlow();
  const patch = <S extends Step>(p: Partial<S>) => f.patchStep<S>(step.uid, p);
  switch (step.type) {
    case "email":
      return <EmailFields step={step} onChange={patch} onToast={f.onToast} mail={f.mail} />;
    case "room":
      return <RoomFields step={step} onChange={patch} onToast={f.onToast} />;
    case "fact":
      return (
        <div className="fe-sent">
          <Pick
            label="Add or remove"
            value={step.op}
            options={[
              { value: "add", label: "Add" },
              { value: "remove", label: "Remove" },
            ]}
            onChange={(v) => patch({ op: v === "remove" ? "remove" : "add" })}
          />
          <span>the tag</span>
          <FlowTag label="The tag to change" value={step.name} onChange={(name) => patch({ name })} />
        </div>
      );
    case "webhook":
      return <WebhookFields step={step} onChange={patch} onToast={f.onToast} />;
    case "notify":
      return <NotifyFields step={step} onChange={patch} />;
    case "wait":
      return <WaitFields step={step} onChange={patch} sessions={f.sessions} />;
    case "branch":
      return <BranchFields step={step} onChange={(ask) => patch<BranchStep>({ ask })} onSwap={() => f.swap(step.uid)} />;
  }
}

/** A card open in place: its kind and a ×, its controls, then Delete step, Discard and Done. */
function StepCard({ kind, children, path, uid }: { kind: Kind; children: ReactNode; path?: ListPath; uid?: string }) {
  const f = useFlow();
  return (
    <div
      className={`fl-node fe-card k-${KIND_CLASS[kind]}${f.fresh && f.fresh === uid ? " new" : ""}`}
      data-fl={uid ?? "trigger"}
      {...(path ? { "data-list": JSON.stringify(path) } : {})}
    >
      <div className="fe-h">
        <span className={`fl-ic ${KIND[kind].tone}`}>
          <Icon name={KIND[kind].icon} />
        </span>
        <b>{kind === "trigger" ? "Start" : KIND[kind].label}</b>
        <span className="sp" />
        <button type="button" className="ibtn sm" title="Close" aria-label="Close" onClick={f.done}>
          <Icon name="x" />
        </button>
      </div>
      <div className="fe-b">{children}</div>
      <div className="fe-foot">
        {kind !== "trigger" && uid ? (
          <button type="button" className="btn q fe-del" onClick={() => f.remove(uid)}>
            <Icon name="trash" />
            Delete step
          </button>
        ) : null}
        <span className="sp" />
        <button type="button" className="btn" onClick={f.discard}>
          Discard
        </button>
        <button type="button" className="btn p" onClick={f.done}>
          <Icon name="check" />
          Done
        </button>
      </div>
    </div>
  );
}

/** One step on the line, closed; or its card, when it is the one open. */
function FlowNode({ step, path }: { step: Step; path: ListPath }) {
  const f = useFlow();
  const { sub, held } = useStepFacts(step, f.people);
  const here = f.people?.at[step.uid] ?? [];
  if (f.open === step.uid) {
    return (
      <StepCard kind={step.type} path={path} uid={step.uid}>
        <StepFields step={step} />
      </StepCard>
    );
  }
  return (
    <div
      className={`fl-node k-${KIND_CLASS[step.type]}${f.dragging === step.uid ? " dragging" : ""}`}
      data-fl={step.uid}
      data-list={JSON.stringify(path)}
      {...(f.editing
        ? {
            role: "button",
            tabIndex: 0,
            "aria-label": `${KIND[step.type].label}: open`,
            "data-act": "open",
            onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => f.onNodeDown(e, step),
            onClick: () => f.onNodeClick(step.uid),
            onKeyDown: (e: { key: string; preventDefault: () => void }) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                f.openCard(step.uid);
              }
            },
          }
        : {})}
    >
      <span className={`fl-ic ${KIND[step.type].tone}`}>
        <Icon name={KIND[step.type].icon} />
      </span>
      <div className="fl-tx">
        <div className="fl-k">{KIND[step.type].label}</div>
        <div className="fl-t">
          <StepTitle step={step} mail={f.mail} />
        </div>
        {sub ? <div className="fl-s">{sub}</div> : null}
        {step.type === "email" && emailNeeds({ subject: step.subject, body_html: step.previewHtml }) === "message" ? <div className="fl-s fl-held warn">No message yet</div> : null}
        {held ? <div className={`fl-s fl-held ${held.tone}`}>{held.line}</div> : null}
        {step.type === "wait" && step.mode === "before" && f.sessions === false ? <div className="fl-s">Skipped here: this room plays any time.</div> : null}
      </div>
      <span />
      <HereBar people={here} waits={step.type === "wait"} paused={f.paused} />
    </div>
  );
}

/** The gap between two steps, with its + and the menu of steps it opens. */
function FlowConn({ path, index, label, tail }: { path: ListPath; index: number; label?: string; tail?: boolean }) {
  const f = useFlow();
  const at = `${listKey(path)}#${index}`;
  const menu = f.editing && f.menuAt === at;
  return (
    <div className={`fl-conn${label ? " lab" : ""}${tail ? " tail" : ""}${menu ? " menuon" : ""}`}>
      {label ? <span className="fl-bl">{label}</span> : null}
      {f.editing ? (
        <button
          type="button"
          className={menu ? "fl-add on" : "fl-add"}
          title="Add a step here"
          aria-label="Add a step here"
          aria-expanded={menu}
          onClick={() => f.setMenuAt(menu ? null : at)}
        >
          <Icon name="plus" />
        </button>
      ) : null}
      {menu ? <StepMenu onPick={(type) => f.insert(path, index, type)} /> : null}
    </div>
  );
}

function FlowList({ list, path, label }: { list: Step[]; path: ListPath; label?: string }) {
  const last = list[list.length - 1];
  return (
    <>
      {list.map((s, i) => (
        <Fragment key={s.uid}>
          <FlowConn path={path} index={i} {...(i === 0 && label ? { label } : {})} />
          {s.type === "branch" ? <CheckRow step={s} path={path} /> : <FlowNode step={s} path={path} />}
        </Fragment>
      ))}
      {last?.type !== "branch" ? (
        <>
          <FlowConn path={path} index={list.length} tail {...(!list.length && label ? { label } : {})} />
          <div className="fl-end">
            <span>
              <Icon name="flag" />
            </span>
            End
          </div>
        </>
      ) : null}
    </>
  );
}

/** A branch: its question on the line, the Yes path under it, and the No path to the side. */
function CheckRow({ step, path }: { step: BranchStep; path: ListPath }) {
  const labels = askPaths(step.ask);
  return (
    <div className="fl-crow">
      <div className="fl-cmain">
        <FlowNode step={step} path={path} />
        <div className="fl-yes">
          <FlowList list={step.yes} path={[...path, { uid: step.uid, arm: "yes" }]} label={`Yes · ${labels.yes}`} />
        </div>
      </div>
      <div className="fl-cside">
        <div className="fl-elbow" />
        <FlowList list={step.no} path={[...path, { uid: step.uid, arm: "no" }]} label={`No · ${labels.no}`} />
      </div>
    </div>
  );
}

/** The first stop: the tag that starts it. */
function WhenStop({ whenSub }: { whenSub: string | undefined }) {
  const f = useFlow();
  const t = f.editor.trigger;
  if (f.open === "trigger") {
    return (
      <StepCard kind="trigger">
        <TriggerFields editor={f.editor} onChange={f.onTrigger} />
      </StepCard>
    );
  }
  const lines = triggerLines(t, f.repeats);
  return (
    <div
      className="fl-node k-when"
      {...(f.editing
        ? {
            role: "button",
            tabIndex: 0,
            "aria-label": "When: open",
            "data-act": "open",
            onClick: () => f.openCard("trigger"),
            onKeyDown: (e: { key: string; preventDefault: () => void }) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                f.openCard("trigger");
              }
            },
          }
        : {})}
    >
      <span className="fl-ic brass">
        <Icon name="tag" />
      </span>
      <div className="fl-tx">
        <div className="fl-k">When</div>
        <div className="fl-t">
          Someone {t.type === "tag_removed" ? "loses" : "gets"} the tag <TagChip name={t.value} />
        </div>
        {lines.length ? <div className="fl-s">{lines.join(" · ")}</div> : null}
        {whenSub ? <div className="fl-s">{whenSub}</div> : null}
      </div>
      <span />
      <HereBar people={f.people?.at.start ?? []} paused={f.paused} />
    </div>
  );
}

/** The + menu: the real step names, with Wait and Branch after a rule. */
function StepMenu({ onPick }: { onPick: (type: StepType) => void }) {
  return (
    <div className="fe-menu" role="menu">
      {STEP_MENU.map((type, i) => (
        <Fragment key={type}>
          {i === 5 ? <hr /> : null}
          <button type="button" role="menuitem" onClick={() => onPick(type)}>
            <Icon name={KIND[type].icon} />
            <span>{KIND[type].label}</span>
          </button>
        </Fragment>
      ))}
    </div>
  );
}

/** On a phone: the flow as an outline of rows; a row opens its step full width. */
function Outline({ open }: { open: string | null }) {
  const f = useFlow();
  const { editor, editing } = f;
  const screen = outlineScreen(editor, open);
  if (screen) {
    return (
      <div className="flp">
        <div className="flp-top">
          <button type="button" className="link" onClick={f.done}>
            <Icon name="left" />
            All steps
          </button>
        </div>
        {screen === "trigger" ? (
          <StepCard kind="trigger">
            <TriggerFields editor={editor} onChange={f.onTrigger} />
          </StepCard>
        ) : (
          <StepCard kind={screen.step.type} path={screen.path} uid={screen.step.uid}>
            <StepFields step={screen.step} />
          </StepCard>
        )}
      </div>
    );
  }
  const row = (step: Step, depth: number) => (
    <button type="button" key={step.uid} className="flp-row" style={{ ["--d" as string]: depth }} onClick={() => f.openCard(step.uid)} disabled={!editing}>
      <span className={`fl-ic ${KIND[step.type].tone}`}>
        <Icon name={KIND[step.type].icon} />
      </span>
      <span className="sp">
        <em>{KIND[step.type].label}</em>
        <b>
          <StepTitle step={step} mail={f.mail} />
        </b>
      </span>
      <Icon name="chev" />
    </button>
  );
  const add = (path: ListPath, index: number, depth: number) => {
    const at = `${listKey(path)}#${index}`;
    if (!editing) return null;
    return (
      <Fragment key={`add-${at}`}>
        <button
          type="button"
          className="flp-add"
          style={{ ["--d" as string]: depth }}
          aria-expanded={f.menuAt === at}
          onClick={() => f.setMenuAt(f.menuAt === at ? null : at)}
        >
          <Icon name="plus" />
          Add a step
        </button>
        {f.menuAt === at ? (
          <div className="flp-sheet" style={{ ["--d" as string]: depth }}>
            <StepMenu onPick={(type) => f.insert(path, index, type)} />
          </div>
        ) : null}
      </Fragment>
    );
  };
  const list = (steps: Step[], path: ListPath, depth: number): ReactNode[] => {
    const out: ReactNode[] = [];
    for (const s of steps) {
      out.push(row(s, depth));
      if (s.type === "branch") {
        const labels = askPaths(s.ask);
        out.push(
          <div key={`${s.uid}-no`} className="flp-lab no" style={{ ["--d" as string]: depth + 1 }}>
            No · {labels.no}
          </div>,
          ...list(s.no, [...path, { uid: s.uid, arm: "no" }], depth + 1),
          <div key={`${s.uid}-yes`} className="flp-lab" style={{ ["--d" as string]: depth + 1 }}>
            Yes · {labels.yes}
          </div>,
          ...list(s.yes, [...path, { uid: s.uid, arm: "yes" }], depth + 1),
        );
      }
    }
    if (steps[steps.length - 1]?.type !== "branch") out.push(add(path, steps.length, depth));
    return out;
  };
  const lines = triggerLines(editor.trigger, f.repeats);
  return (
    <div className="flp">
      <button type="button" className="flp-row when" style={{ ["--d" as string]: 0 }} onClick={() => f.openCard("trigger")} disabled={!editing}>
        <span className="fl-ic brass">
          <Icon name="tag" />
        </span>
        <span className="sp">
          <em>When</em>
          <b>
            Someone {editor.trigger.type === "tag_removed" ? "loses" : "gets"} the tag <TagChip name={editor.trigger.value} />
          </b>
          {lines.length ? <small>{lines.join(" · ")}</small> : null}
        </span>
        <Icon name="chev" />
      </button>
      {list(editor.steps, [], 0)}
    </div>
  );
}
